'use client';

import { useCallback, useMemo, useState } from 'react';
import { useCatalogCategories } from './use-catalog';
import { parseBrandToken } from '../lib/brand-tokens';
import type { BrandSelection } from '@/types/brand';

/**
 * Where the Brands picker is standing, and every way it can move.
 *
 * Lifted out of the picker body so the SHEET's own Back button can drive it: the
 * picker is a four-level tree, and a Back that always closed the sheet forced a
 * second "< Back" row inside the content — two back buttons on one screen, one
 * leaving and one going up a level, with nothing to say which was which. Now the
 * single chevron in the header walks up the tree and only closes the sheet from
 * the top. Mirrors Apple (`BrandCatalogStore`) and Android (`BrandsViewModel`),
 * where the same state has always lived outside the view.
 */
export interface BrandsNavigation {
  categorySlug: string | null;
  brandId: string | null;
  lineId: string | null;
  /** Chosen value slug per axis slug — a library may be faceted by more than one. */
  axisChoice: Record<string, string>;
  setAxisChoice: (choice: Record<string, string>) => void;
  /**
   * The axis values a revealed pick was made on. Opening a line normally lands
   * on its first value; jumping to an existing pick must land on the ones that
   * pick actually carries, or the row it points at reads as unselected and the
   * user is looking at a different product with the same name.
   */
  preferredSlugs: string[];
  searchInput: string;
  setSearchInput: (value: string) => void;
  openCategory: (slug: string) => void;
  openBrand: (id: string) => void;
  openLine: (id: string) => void;
  /** Jump to where a pick lives, instead of removing it. */
  reveal: (entry: BrandSelection) => void;
  /** One step up. `false` at the top, where Back belongs to the sheet. */
  goBack: () => boolean;
  /** Straight back to the list of libraries — the breadcrumb's root segment. */
  goToRoot: () => void;
}

export function useBrandsNavigation(): BrandsNavigation {
  const { data: catalog } = useCatalogCategories();
  const categories = useMemo(() => catalog?.categories ?? [], [catalog]);

  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [lineId, setLineId] = useState<string | null>(null);
  const [axisChoice, setAxisChoice] = useState<Record<string, string>>({});
  const [preferredSlugs, setPreferredSlugs] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const openCategory = useCallback((slug: string) => {
    setCategorySlug(slug);
    setBrandId(null);
    setLineId(null);
    setAxisChoice({});
    setPreferredSlugs([]);
    setSearchInput('');
  }, []);

  const openBrand = useCallback((id: string) => {
    setBrandId(id);
    setLineId(null);
    setAxisChoice({});
    setPreferredSlugs([]);
  }, []);

  const openLine = useCallback((id: string) => {
    setLineId(id);
    setAxisChoice({});
    setPreferredSlugs([]);
  }, []);

  const reveal = useCallback(
    (entry: BrandSelection) => {
      // A library with no navigable line level still hangs its products off one
      // implicit line, and that id must not reach the navigation state: it is
      // plumbing, so a Back press would spend itself clearing a level the user
      // never saw. Same rule the search results already follow.
      const target = categories.find(
        (candidate) => candidate.attributes.slug === entry.attributes.category
      );

      setSearchInput('');
      setCategorySlug(entry.attributes.category);
      setBrandId(entry.attributes.brand_id);
      setLineId(
        target?.attributes.has_line_level ? entry.attributes.line_id : null
      );
      setAxisChoice({});
      setPreferredSlugs(parseBrandToken(entry.id)?.axisValueSlugs ?? []);
    },
    [categories]
  );

  const goBack = useCallback(() => {
    if (categorySlug === null) return false;

    setSearchInput('');

    if (lineId) {
      setLineId(null);
      setAxisChoice({});
      return true;
    }
    if (brandId) {
      setBrandId(null);
      return true;
    }

    setCategorySlug(null);
    return true;
  }, [categorySlug, brandId, lineId]);

  const goToRoot = useCallback(() => {
    setSearchInput('');
    setCategorySlug(null);
    setBrandId(null);
    setLineId(null);
    setAxisChoice({});
    setPreferredSlugs([]);
  }, []);

  return {
    categorySlug,
    brandId,
    lineId,
    axisChoice,
    setAxisChoice,
    preferredSlugs,
    searchInput,
    setSearchInput,
    openCategory,
    openBrand,
    openLine,
    reveal,
    goBack,
    goToRoot,
  };
}
